/**
 * Client fetch/submit pour les pages externes tokenisées (accès sans compte).
 *
 * Brique réutilisée par toutes les pages externes (CRA validateur externe, portage…).
 * On utilise `fetch` brut (et non l'instance axios authentifiée de l'app) : ces pages
 * sont publiques, sans session ni intercepteur d'auth. Le token circule en query param.
 */

import { getNextjsApiBaseUrl } from '@/config/apiBaseUrl'

/** Base URL API pour les pages externes — même résolution que axios (tunnel bloby, prod, proxy local). */
export function getExternalApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    return getNextjsApiBaseUrl()
  }
  return (
    import.meta.env.VITE_NEXTJS_API_URL ||
    import.meta.env.VITE_API_URL ||
    ''
  ).replace(/\/+$/, '')
}

/** Erreur enrichie du statut HTTP + données ({ already_used, action }) renvoyées par l'API. */
export type ExternalPageError = Error & {
  status?: number
  data?: { already_used?: boolean; action?: string; subject_type?: string }
}

type ApiEnvelope<T> = { code?: number; message?: string; data?: T }

/**
 * GET des données d'une page externe pour un token donné.
 * @param parseData transformation optionnelle des données brutes (normalisation)
 */
export async function fetchExternalPageData<TData>(
  endpoint: string,
  token: string,
  parseData?: (raw: unknown) => TData,
  options?: { credentials?: RequestCredentials }
): Promise<TData> {
  const url = `${getExternalApiBaseUrl()}${endpoint}?token=${encodeURIComponent(token)}`
  const res = await fetch(url, {
    credentials: options?.credentials,
  })

  let json: ApiEnvelope<TData> = {}
  try {
    json = await res.json()
  } catch {
    throw new Error('Invalid response from server')
  }

  if (!res.ok) {
    const error = new Error(json.message || 'Failed to load') as ExternalPageError
    error.status = res.status
    error.data = (json as ApiEnvelope<unknown>).data as ExternalPageError['data']
    throw error
  }

  const data = json.data
  if (data == null || typeof data !== 'object') {
    throw new Error('Invalid response from server')
  }
  return parseData ? parseData(data) : (data as TData)
}

/** POST d'une action sur une page externe. Le token est injecté dans le corps automatiquement. */
export async function submitExternalPageAction<TResult = unknown>(
  endpoint: string,
  token: string,
  body: Record<string, unknown>,
  options?: { credentials?: RequestCredentials }
): Promise<TResult> {
  const res = await fetch(`${getExternalApiBaseUrl()}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: options?.credentials,
    body: JSON.stringify({ token, ...body }),
  })
  const json: ApiEnvelope<TResult> = await res.json()
  if (!res.ok) {
    throw new Error(json.message || 'Failed to submit')
  }
  return json.data as TResult
}
